import React from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { X, ChevronDown } from "lucide-react";

/**
 * Iter-538 — Trade-Me-parity quick filter CHIP BAR above the results.
 * Chips are a second entry point into the SAME Refine rail + URL params:
 * tap = scroll the matching rail section into view; ✕ = clear only that
 * group and refetch immediately (params live in the URL already).
 * Schema-driven: vehicle chips only when the schema declares them.
 * Founder exclusions: no Payment chip, no Safety rating.
 */

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const NEW_SET = "brand-new,new";
const USED_SET = "like-new,excellent,good,fair,used";

function fmtNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-NZ") : v;
}

export default function QuickChips({ params, categorySlug, onClear }) {
  const schemaQ = useQuery({
    queryKey: ["quick-chips-schema", categorySlug || ""],
    enabled: !!categorySlug,
    staleTime: 10 * 60_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/filter-schema", { params: { slug: categorySlug } })).data,
  });
  const schema = schemaQ.data;
  const isVehicle = (schema?.vehicle_filters || []).length > 0;
  const hasOdo = (schema?.range_filters || []).some((f) => f.key === "Kilometres");
  const hasFuel = (schema?.attribute_filters || []).some((f) => f.key === "Fuel Type");
  const get = (k) => params.get(k) || "";

  const conditionLabel = () => {
    const c = get("condition");
    if (!c) return "";
    if (c === NEW_SET || c === "new" || c === "brand-new") return "New";
    if (c === USED_SET || c === "used") return "Used";
    return c.split(",").length > 2 ? "Used" : c;
  };
  const span = (a, b, pre = "", post = "") =>
    a || b ? `${pre}${a ? fmtNum(a) : "any"}–${b ? fmtNum(b) : "any"}${post}` : "";

  const chips = [
    { key: "category", label: "Category", section: "category", value: schema?.category?.name || (categorySlug ? categorySlug : ""), clears: { category: "" } },
    { key: "location", label: "Location", section: "region",
      value: get("area") || get("city") || get("region"),
      clears: { region: "", city: "", area: "" } },
    { key: "condition", label: "New & used", section: "condition", value: conditionLabel(), clears: { condition: "" } },
    ...(isVehicle ? [
      { key: "make", label: "Make", section: "make", value: get("make"), clears: { make: "" } },
      { key: "year", label: "Year", section: "year", value: span(get("min_year"), get("max_year")), clears: { min_year: "", max_year: "" } },
    ] : []),
    { key: "price", label: "Price", section: "price", value: span(get("min_price"), get("max_price"), "$"), clears: { min_price: "", max_price: "" } },
    ...(hasOdo ? [{
      key: "odometer", label: "Odometer", section: "odometer-km",
      value: span(get("attrmin.Kilometres"), get("attrmax.Kilometres"), "", " km"),
      clears: { "attrmin.Kilometres": "", "attrmax.Kilometres": "" },
    }] : []),
    ...(hasFuel ? [{
      key: "fuel", label: "Fuel type", section: "fuel-type",
      value: get("attr.Fuel Type").split(",").filter(Boolean).join("/"),
      clears: { "attr.Fuel Type": "" },
    }] : []),
    { key: "seller", label: "Seller type", section: "seller-type",
      value: get("seller_type") === "dealer" ? "Dealer" : get("seller_type") === "private" ? "Private" : "",
      clears: { seller_type: "" } },
  ];

  const openSection = (section) => {
    const el =
      document.querySelector(`[data-refine-section="${section}"]`) ||
      document.querySelector('[data-testid="schema-filter-rail"]');
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:overflow-visible" data-testid="quick-chips-bar">
      {chips.map((c) => {
        const active = !!c.value;
        return (
          <span
            key={c.key}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border text-xs font-semibold transition-colors ${
              active
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            <button
              type="button"
              data-testid={`chip-${c.key}`}
              onClick={() => openSection(c.section)}
              className="inline-flex items-center gap-1 pl-3 py-1.5 rounded-full"
            >
              {c.label}: {c.value || (c.key === "year" || c.key === "price" || c.key === "odometer" ? "Any" : "All")}
              <ChevronDown className="w-3 h-3 opacity-70" />
            </button>
            {active && (
              <button
                type="button"
                aria-label={`Clear ${c.label}`}
                data-testid={`chip-clear-${c.key}`}
                onClick={() => onClear(c.clears)}
                className="pr-2.5 pl-0.5 py-1.5 rounded-full hover:opacity-70"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {!active && <span className="pr-2" />}
          </span>
        );
      })}
    </div>
  );
}
